import { Loading } from '@shape-ds/components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlotScaling } from 'components';
import { Dayjs, dayjs } from 'lib';
import { DateFilters, InfoMessage } from '@shape/lighthouse-components';
import useFiltersLocalStorage from 'components/LineCharts/hooks/useFiltersLocalStorage';
import { usePlotViewData } from '../../hooks/usePlotViewData';
import { changePeriod, useDatesHelper } from '../../helpers';
import './style.scss';

type PlotScalingWrapperProps = {
  alarmDateRange?: { start: Dayjs; end: Dayjs };
  modelName: string;
  hasExpandIcon?: boolean;
  showTitle?: boolean;
};

const DEFAULT_PERIOD = '720';
export const PlotScalingWrapper = ({
  modelName,
  alarmDateRange,
  hasExpandIcon = false,
  showTitle = true,
}: PlotScalingWrapperProps) => {
  const { t } = useTranslation();
  const { buildUTCDateRange } = useDatesHelper();
  const CHART_ID = `event-plot-${modelName}`;
  const { saveOnLocalStorage, getSavedFilters } = useFiltersLocalStorage(CHART_ID);
  const localFilters = getSavedFilters(CHART_ID);
  const [period, setPeriod] = useState<string>(localFilters.period ?? DEFAULT_PERIOD);
  const periodOptions = ['30', '90', '180'].map((option) => ({
    label: t('components:date_filter.last_n_days', { days: option }),
    value: (+option * 24).toString(),
  }));
  const defaultDateRange =
    localFilters.period === 'custom'
      ? { start: dayjs(localFilters.dateRange?.start), end: dayjs(localFilters.dateRange?.end) }
      : buildUTCDateRange(period);

  const [dateRange, setDateRange] = useState(
    alarmDateRange && period !== 'custom' ? changePeriod(alarmDateRange, period) : defaultDateRange,
  );

  const onChangePeriod = (
    value: string,
    dateRange?: {
      start: Dayjs;
      end: Dayjs;
    },
  ) => {
    saveOnLocalStorage(
      { period: value, dateRange: dateRange ?? buildUTCDateRange(value) },
      CHART_ID,
    );
    setPeriod(value);
    setDateRange(dateRange ?? buildUTCDateRange(value));
  };
  const { data, error, isLoading } = usePlotViewData({
    modelName,
    start: dateRange.start,
    end: dateRange.end,
  });

  if (isLoading) {
    return (
      <div className="scaling-error-container">
        <Loading />
      </div>
    );
  }

  if (data?.scaling_data === null || data?.scaling_data === undefined) {
    return (
      <div className="scaling-error-container">
        <InfoMessage
          type="warning"
          title={t('messages:no_data.title')}
          message={t('messages:no_data.message')}
          data-testid="no-data-message"
        />
      </div>
    );
  }

  return (
    <>
      <div className={`${showTitle ? 'box-top' : 'box-top-no-title'}`}>
        {showTitle && <h2 className="box-title">{t('model_page:sections.amoeba')}</h2>}
        <DateFilters
          periodOptions={periodOptions}
          defaultPeriod={period}
          enableCalendar
          enableCustomPeriod
          handlePeriodChange={(period) => onChangePeriod(period)}
          data-testid="date-filters"
          handleCalendarChange={(dateRange) =>
            onChangePeriod('custom', {
              end: dayjs(dateRange.endDate),
              start: dayjs(dateRange.startDate),
            })
          }
          defaultDateRange={
            period === 'custom' && dateRange.start ? [dateRange.start, dateRange.end] : undefined
          }
        />
      </div>
      <PlotScaling
        period={[dayjs(dateRange.start), dayjs(dateRange.start)]}
        modelName={modelName}
        modelType={data?.model_type || ''}
        modelDate={data?.model_date || ''}
        hasTitle={false}
        hasExpandIcon={hasExpandIcon}
        data={data}
        error={error}
        isLoading={isLoading}
        data-testid="plot-scaling"
      />
    </>
  );
};
